import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaSun, FaMoon } from "react-icons/fa";
import { useAuth } from "../../auth/AuthContext";

/* ─── Spinner Component ──────────────────────────────────── */
const Spinner = ({ size = "h-4 w-4" }) => (
  <svg className={`animate-spin ${size} text-blue-500`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const ProfileDropdownMenu = ({ user, toggleDarkMode, darkMode, onClose }) => {
  const { logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Prevent body scroll when logout modal is open
  useEffect(() => {
    if (showLogoutModal && isLoggingOut) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showLogoutModal, isLoggingOut]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      setIsLoggingOut(false);
    }
  };

  const handleBackdropClick = (e) => {
    // Prevent closing modal by clicking backdrop if logging out
    if (!isLoggingOut && e.target === e.currentTarget) {
      setShowLogoutModal(false);
    }
  };

  return (
    <>
      <div className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-200 py-2 z-50 transition-all duration-300 dark:bg-gray-800 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">
            {(user.first_name || user.firstName) ? `${user.first_name || user.firstName} ${user.last_name || user.lastName || ""}`.trim() : user.name || user.username || "User"}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-200">
            {user.email || ""}
          </p>
          {user.role && (
            <span className="inline-block bg-blue-100 text-[#2563EB] text-xs px-2 py-1 rounded-full mt-1 dark:bg-blue-200 dark:text-blue-900">
              {user.role}
            </span>
          )}
        </div>
        <Link
          to="/messages"
          className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#2563EB] transition-colors dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-blue-400"
          onClick={onClose}
        >
          Messages
        </Link>
        <Link
          to="/account"
          className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#2563EB] transition-colors dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-blue-400"
          onClick={onClose}
        >
          Account
        </Link>
        <Link
          to="/support"
          className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#2563EB] transition-colors dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-blue-400"
          onClick={onClose}
        >
          Help & Support
        </Link>
        <div
          className="group flex items-center justify-between px-4 py-3 cursor-pointer transition"
          onClick={toggleDarkMode}
        >
          <span className="text-sm text-gray-700 dark:text-gray-200 transition-colors">
            Dark Mode
          </span>
          <div className="flex items-center space-x-2">
            {darkMode ? (
              <FaSun className="text-yellow-400 text-lg dark:text-yellow-300" />
            ) : (
              <FaMoon className="text-gray-800 text-lg dark:text-gray-300" />
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 mt-2 pt-2 dark:border-gray-700">
          <button
            className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors dark:hover:bg-red-900 dark:hover:text-red-400"
            onClick={() => setShowLogoutModal(true)}
          >
            Logout
          </button>
        </div>
      </div>

      {showLogoutModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-[9999]"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            animation: "fadeIn 0.3s ease-in-out forwards",
          }}
          onClick={() => !isLoggingOut && setShowLogoutModal(false)}
        >
          {/* ── DESKTOP POPUP ── */}
          <div 
            className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-96 relative"
            style={{
              animation: "slideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
            }}
          >
            {/* Loading overlay during logout */}
            {isLoggingOut && (
              <div className="absolute inset-0 bg-white/40 dark:bg-gray-800/40 rounded-xl flex items-center justify-center backdrop-blur-sm z-10">
                <div className="text-center">
                  <Spinner size="h-10 w-10" />
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 font-semibold">Logging out...</p>
                </div>
              </div>
            )}

            

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
              Confirm Logout
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-center text-sm">
              Are you sure you want to log out? You'll need to log in again to access your account.
            </p>
            
            <div className="flex gap-3">
              <button 
                disabled={isLoggingOut}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
              >
                Cancel
              </button>
              <button
                disabled={isLoggingOut}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={handleLogout}
              >
                {isLoggingOut ? (
                  <>
                    <Spinner size="h-5 w-5" />
                    <span>Logging out...</span>
                  </>
                ) : (
                  "Logout"
                )}
              </button>
            </div>
          </div>

          {/* ── MOBILE POPUP ── */}
          <div 
            className="md:hidden bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full fixed bottom-0 left-0 relative m-4"
            style={{
              animation: "slideUpMobile 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
              maxHeight: "80vh",
              overflowY: "auto",
              width: "calc(100% - 2rem)",
              bottom: "auto",
              left: "1rem",
            }}
          >
            {/* Loading overlay during logout */}
            {isLoggingOut && (
              <div className="absolute inset-0 bg-white/40 dark:bg-gray-800/40 rounded-2xl flex items-center justify-center backdrop-blur-sm z-10">
                <div className="text-center">
                  <Spinner size="h-12 w-12" />
                  <p className="text-base text-gray-700 dark:text-gray-300 mt-4 font-bold">Logging out...</p>
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
              Logout?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-center text-sm leading-relaxed">
              You'll need to log in again to access your account and all your listings.
            </p>
            
            <div className="space-y-3">
              <button
                disabled={isLoggingOut}
                className="w-full px-4 py-3.5 text-base font-bold text-white bg-red-500 rounded-xl hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-95"
                onClick={handleLogout}
              >
                {isLoggingOut ? (
                  <>
                    <Spinner size="h-5 w-5" />
                    <span>Logging out...</span>
                  </>
                ) : (
                  "Yes, Logout"
                )}
              </button>
              <button 
                disabled={isLoggingOut}
                className="w-full px-4 py-3.5 text-base font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
              >
                Keep Me Logged In
              </button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }

            @keyframes slideUpMobile {
              from {
                opacity: 0;
                transform: translateY(100px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
};


export default ProfileDropdownMenu;
